/* eslint-disable no-unused-vars */
'use strict'

enum ChatbotErrorCode {
  MAXTOKEN = 'maxToken',
}

export default ChatbotErrorCode

export enum ButtonMode {
  ChatBar = 'chat-bar',
  ClassicButton = 'classic-button',
}

export enum ButtonSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export enum ButtonStyle {
  Sharp = 'sharp',
  Rounded = 'rounded',
  Pill = 'pill',
}

export enum ButtonPosition {
  BottomLeft = 'bottom-left',
  BottomRight = 'bottom-right',
  TopRight = 'top-right',
  TopLeft = 'top-left',
}

export enum ButtonEffect {
  None = 'none',
  Pulsate = 'pulsate',
  Shine = 'shine',
}

export enum ButtonDesignField {
  Mode = 'mode',
  Size = 'size',
  Style = 'style',
  Position = 'position',
  Effect = 'effect',
}

export interface IButtonDesignSettings {
  mode?: ButtonMode
  size?: ButtonSize
  style?: ButtonStyle
  position?: ButtonPosition
  effect?: ButtonEffect
}

export const DEFAULT_PLACEHOLDER_DURATION = 7000
export const FAQ_PLACEHOLDER_DURATION = 4000
export const TYPING_DEBOUNCE_DELAY = 500

export const ChatbotEventTypes = {
  MESSAGE_SENT: 'kotaeMessageSent',
  MICROPHONE_ON: 'kotaeMicrophoneOn',
  MICROPHONE_OFF: 'kotaeMicrophoneOff',
  VOICE_CHAT_ON: 'kotaeVoiceChatOn',
  VOICE_CHAT_OFF: 'kotaeVoiceChatOff',
  // Lead signals for the host page's analytics (e.g. a GTM dataLayer). Type only: never contact details or chat text,
  // since these go to any page that embeds the widget.
  BOOKING_SHOWN: 'kotaeBookingShown',
  ESCALATION_SHOWN: 'kotaeEscalationShown',
  ESCALATION_SUBMITTED: 'kotaeEscalationSubmitted',
} as const

export type ChatbotEventType = (typeof ChatbotEventTypes)[keyof typeof ChatbotEventTypes]

// Layout messages exchanged with the embed script. Kept in sync with LAYOUT_EVENT_TYPES in
// public/embed/index.min.js, which cannot import from here.
export const ChatbotLayoutEventTypes = {
  PARENT_VIEWPORT_WIDTH: 'PARENT_VIEWPORT_WIDTH',
  REQUEST_VIEWPORT_WIDTH: 'REQUEST_VIEWPORT_WIDTH',
  CHATBAR_RESIZE: 'CHATBAR_RESIZE',
  PANEL_EXPAND: 'PANEL_EXPAND',
  PANEL_RESTORE: 'PANEL_RESTORE',
  HOST_VIEWPORT: 'HOST_VIEWPORT',
  REQUEST_HOST_VIEWPORT: 'REQUEST_HOST_VIEWPORT',
  PANEL_SIDE_OPEN: 'PANEL_SIDE_OPEN',
  PANEL_SIDE_CLOSE: 'PANEL_SIDE_CLOSE',
  // Visitor reading controls (K-389). Panel -> embed: ask for the current preferences, or report a
  // change. Embed -> panel: the preferences as the embed applied and stored them.
  READING_PREFS_REQUEST: 'READING_PREFS_REQUEST',
  READING_PREFS_CHANGE: 'READING_PREFS_CHANGE',
  READING_PREFS_APPLIED: 'READING_PREFS_APPLIED',
} as const

export type ChatbotLayoutEventType = (typeof ChatbotLayoutEventTypes)[keyof typeof ChatbotLayoutEventTypes]

// Visitor text size (K-389): 小 100%, 中 125%, 大 150%, cycled in this order by one button.
// Kept in sync with TEXT_SCALES in public/embed/index.min.js, which cannot import from here.
export const TEXT_SCALES = [1, 1.25, 1.5] as const

export type TextScale = (typeof TEXT_SCALES)[number]

export const DEFAULT_TEXT_SCALE: TextScale = 1

// Width of the expanded chat window (K-381 section 2). Kept in sync with the expanded rule in
// public/embed/index.min.css. The booking side panel uses it to decide whether the calendar fits
// beside the chat (K-379 3.8).
export const EXPANDED_CHAT_WIDTH_PX = 740

// The embed puts a stored, non-default text size on the panel's iframe src, so the panel's first
// paint is already at that size.
export const TEXT_SCALE_QUERY_PARAM = 'textScale'

// Multiplies the root font size of the chat route, so the visitor's own browser or OS font size
// setting is kept and the chosen size is added on top of it.
export const TEXT_SCALE_CSS_VARIABLE = '--kotae-text-scale'

export const isTextScale = (value: unknown): value is TextScale =>
  typeof value === 'number' && (TEXT_SCALES as readonly number[]).includes(value)

// Anything that is not one of the three sizes (missing, corrupt, from an older format, or a
// hand-edited query string) falls back to the default instead of reaching the page.
export const parseTextScale = (value: unknown): TextScale => {
  const scale = typeof value === 'string' && value.trim() !== '' ? Number(value) : value
  return isTextScale(scale) ? scale : DEFAULT_TEXT_SCALE
}

// 小 -> 中 -> 大 -> back to 小.
export const getNextTextScale = (scale: TextScale): TextScale =>
  TEXT_SCALES[(TEXT_SCALES.indexOf(scale) + 1) % TEXT_SCALES.length] ?? DEFAULT_TEXT_SCALE

export const CHATBOT_ACCESS_TOKEN = 'chatbotAccessToken'

export const REALTIME_VOICE_CHAT_MAX_DURATION = 300 // 5 minutes
export const REALTIME_VOICE_DEFAULT_LANGUAGE = 'English'
export const REALTIME_VOICE_CHAT_LANGUAGE_EXPIRY_LOCALSTORAGE = 7 * 24 * 60 * 60 * 1000 // 7 days
export const CHATBOT_ACCESS_TOKEN_EXPIRY_LOCALSTORAGE = 60 * 60 * 1000 // 1 hour
